/*
 * ATHL8, Cookie Consent
 *
 * Slim sticky bottom bar + preferences modal, backed by Shopify's
 * Customer Privacy API (window.Shopify.customerPrivacy). We don't
 * manage cookies ourselves, Shopify handles persistence and regional
 * detection (EU/UK GDPR, CCPA, etc.).
 *
 * Surfaces:
 *  - <cookie-consent> custom element rendered on every page (injected
 *    via sections/overlay-group.json). Owns both banner + modal.
 *  - window.ATHL8.openCookiePreferences(). Global hook for footer link.
 *
 * Events fired:
 *  - 'athl8:consent-changed' on document, detail = { analytics, marketing,
 *    preferences, sale_of_data }. Pixel loaders can listen and load
 *    scripts conditionally.
 *
 * Compliance notes:
 *  - No tracking pixels fire before explicit consent (Shopify handles
 *    Shopify Analytics gating; site-specific pixels go through
 *    snippets/pixel-loaders.liquid which reads the same API).
 *  - Accept and Reject buttons have equal visual weight (CNIL/AP guidance).
 *  - Necessary category is locked-on (not opt-outable).
 *  - User can re-open and change consent via footer "Cookie settings".
 */

(() => {
  'use strict';

  // ATHL8-owned marker cookie: set once the visitor has chosen consent,
  // so subsequent visits don't re-show the banner even when Shopify's
  // shouldShowGDPRBanner() returns false (e.g. visitor outside GDPR
  // region or Customer Privacy API not configured).
  const SEEN_COOKIE = 'athl8_consent_seen';
  // Consent the visitor chose before Shopify's Customer Privacy API was
  // reachable. Stashed here and replayed into setTrackingConsent the moment
  // the API loads, so a fast click or a slow API never strands consent where
  // the Shopify-managed pixels (Meta app pixel, Shopify Analytics) cannot
  // read it.
  const PENDING_KEY = 'athl8_consent_pending';

  function readCookie(name) {
    const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }
  function writeCookie(name, value, days = 365) {
    const exp = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${exp}; path=/; SameSite=Lax`;
  }

  // Resolve Shopify's Customer Privacy API right now, or null if it is not
  // usable yet. Read live (never cached) so a save always reaches the API the
  // instant it exists, even if it loaded after our initial probe.
  function getPrivacyApi() {
    const cp = window.Shopify && window.Shopify.customerPrivacy;
    return cp && typeof cp.setTrackingConsent === 'function' ? cp : null;
  }

  // -----------------------------------------------------------------
  // Customer Privacy API readiness
  // -----------------------------------------------------------------
  // The API is loaded by Shopify via {{ content_for_header }} but may
  // arrive after our element upgrades. Poll briefly, then call cb with
  // the api (or null if it never showed up).
  function whenPrivacyReady(cb, timeoutMs = 4000) {
    const start = Date.now();
    (function tick() {
      if (window.Shopify && window.Shopify.customerPrivacy &&
          typeof window.Shopify.customerPrivacy.setTrackingConsent === 'function') {
        cb(window.Shopify.customerPrivacy);
        return;
      }
      if (Date.now() - start > timeoutMs) {
        cb(null);
        return;
      }
      setTimeout(tick, 100);
    })();
  }

  // -----------------------------------------------------------------
  // Focus trap helper (lifted pattern from assets/modal.js).
  // -----------------------------------------------------------------
  function focusableIn(container) {
    return container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
  }

  function trap(container, restoreEl) {
    const elements = focusableIn(container);
    if (!elements.length) return () => {};
    const first = elements[0];
    const last = elements[elements.length - 1];
    function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    container.addEventListener('keydown', handler);
    setTimeout(() => first.focus(), 50);
    return () => {
      container.removeEventListener('keydown', handler);
      if (restoreEl && typeof restoreEl.focus === 'function') restoreEl.focus();
    };
  }

  // -----------------------------------------------------------------
  // <cookie-consent> web component
  // -----------------------------------------------------------------
  class CookieConsent extends HTMLElement {
    connectedCallback() {
      this.banner = this.querySelector('[data-cookie-banner]');
      this.modal = this.querySelector('[data-cookie-modal]');
      this.modalBackdrop = this.querySelector('[data-cookie-modal-backdrop]');
      this.toggles = {
        functional: this.querySelector('[data-cookie-toggle="functional"]'),
        analytics: this.querySelector('[data-cookie-toggle="analytics"]'),
        marketing: this.querySelector('[data-cookie-toggle="marketing"]'),
      };
      this._releaseFocus = null;
      this._lastTrigger = null;

      // Bind handlers immediately so buttons are responsive the moment the
      // component upgrades. A click before the Customer Privacy API loads is
      // stashed and replayed by syncPendingConsent(), never lost.
      this.bind();

      // Expose global re-open hook for footer link early: so a quick
      // footer-link click before the API resolves still opens the modal.
      window.ATHL8 = window.ATHL8 || {};
      window.ATHL8.openCookiePreferences = () => this.openModal();

      // Show the banner immediately for first-time visitors instead of
      // waiting on the Customer Privacy API (which can take up to the
      // whenPrivacyReady timeout to resolve). The storefront targets NL/EU,
      // so anyone who has not chosen yet always sees the banner. Surfacing it
      // at once, rather than seconds later, is the single biggest lever on
      // consent rate: a banner that appears after the visitor has already
      // scrolled or bounced never earns a click, which suppresses every
      // marketing pixel and tanks landing-page-view tracking. showBanner() is
      // idempotent, so the whenPrivacyReady pass below can safely re-confirm.
      if (readCookie(SEEN_COOKIE) !== '1') this.showBanner();

      // Actively load Shopify's Customer Privacy API so it is present even
      // when the storefront did not inject it eagerly. Falls through to the
      // passive poll below when loadFeatures is unavailable.
      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          () => this.onPrivacyReady(getPrivacyApi())
        );
      }
      whenPrivacyReady((api) => this.onPrivacyReady(api));
    }

    onPrivacyReady(api) {
      if (api && !this.api) {
        this.api = api;
        this.refreshTogglesFromConsent();
      }
      // Replay any choice the visitor made before the API was reachable, so
      // an earlier fast click or slow-loading API still ungates the pixels.
      this.syncPendingConsent();
      const alreadyChose = readCookie(SEEN_COOKIE) === '1';
      if (this.api) {
        // Show if Shopify says we must (GDPR region) or the visitor has not
        // chosen yet. showBanner() is idempotent, so repeat calls are safe.
        if (this.api.shouldShowGDPRBanner() || !alreadyChose) this.showBanner();
      } else if (!alreadyChose) {
        // API still not reachable. Surface the banner anyway so the visitor
        // can choose; the choice is stashed and replayed once the API loads.
        console.warn('[ATHL8] Customer Privacy API unavailable. Consent will be replayed once it loads.');
        this.showBanner();
      }
    }

    // Push a previously stranded choice into Shopify so the consent-gated
    // pixels (Meta app pixel, Shopify Analytics) finally see it. No-op when
    // the API is absent or there is nothing pending.
    syncPendingConsent() {
      const api = this.api || getPrivacyApi();
      if (!api) return;
      this.api = api;
      let pending = null;
      try { pending = JSON.parse(localStorage.getItem(PENDING_KEY) || 'null'); } catch (_) {}
      if (!pending) return;
      api.setTrackingConsent(pending, () => {
        try { localStorage.removeItem(PENDING_KEY); } catch (_) {}
      });
    }

    bind() {
      this.querySelectorAll('[data-cookie-action="accept-all"]').forEach((btn) => {
        btn.addEventListener('click', () => this.saveConsent({ analytics: true, marketing: true, preferences: true, sale_of_data: true }));
      });
      this.querySelectorAll('[data-cookie-action="reject-all"]').forEach((btn) => {
        btn.addEventListener('click', () => this.saveConsent({ analytics: false, marketing: false, preferences: false, sale_of_data: false }));
      });
      this.querySelectorAll('[data-cookie-action="open-preferences"]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          this._lastTrigger = e.currentTarget;
          this.openModal();
        });
      });
      this.querySelectorAll('[data-cookie-action="save-preferences"]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const consent = {
            analytics: !!(this.toggles.analytics && this.toggles.analytics.checked),
            marketing: !!(this.toggles.marketing && this.toggles.marketing.checked),
            preferences: !!(this.toggles.functional && this.toggles.functional.checked),
            // sale_of_data tracks the same "share my data for ads" intent
            // as marketing: keep them aligned unless we add a separate UI.
            sale_of_data: !!(this.toggles.marketing && this.toggles.marketing.checked),
          };
          this.saveConsent(consent);
        });
      });
      this.querySelectorAll('[data-cookie-action="close-modal"]').forEach((btn) => {
        btn.addEventListener('click', () => this.closeModal());
      });
      if (this.modalBackdrop) {
        this.modalBackdrop.addEventListener('click', () => this.closeModal());
      }
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal && this.modal.getAttribute('aria-hidden') === 'false') {
          this.closeModal();
        }
      });
    }

    refreshTogglesFromConsent() {
      if (!this.api || !this.api.currentVisitorConsent) return;
      const c = this.api.currentVisitorConsent() || {};
      // currentVisitorConsent returns strings ("yes" | "no" | "") in some SDK
      // versions and booleans in others: normalize.
      const truthy = (v) => v === true || v === 'yes' || v === '1';
      if (this.toggles.analytics) this.toggles.analytics.checked = truthy(c.analytics);
      if (this.toggles.marketing) this.toggles.marketing.checked = truthy(c.marketing);
      if (this.toggles.functional) this.toggles.functional.checked = truthy(c.preferences);
    }

    showBanner() {
      if (!this.banner || this.banner.classList.contains('is-visible')) return;
      this.banner.removeAttribute('hidden');
      // Force the translateY(100%) start frame to be computed before flipping
      // to translateY(0), so the slide-up animates instead of snapping (a
      // single rAF can coalesce both into one style recalc).
      void this.banner.offsetHeight;
      requestAnimationFrame(() => this.banner.classList.add('is-visible'));
      console.info('[ATHL8] Cookie banner shown');
    }

    hideBanner() {
      if (!this.banner) return;
      this.banner.classList.remove('is-visible');
      // After transition end, hide for a11y / tab order. Filter on
      // target + propertyName because transitionend bubbles from child
      // elements (e.g. button hover transitions) and would otherwise
      // prematurely hide the banner.
      const onEnd = (e) => {
        if (e.target !== this.banner || e.propertyName !== 'transform') return;
        this.banner.setAttribute('hidden', '');
        this.banner.removeEventListener('transitionend', onEnd);
      };
      this.banner.addEventListener('transitionend', onEnd);
      // Safety net: even with prefers-reduced-motion (no transition fires)
      // or unusual environments, ensure we hide within a bounded window.
      setTimeout(() => {
        if (!this.banner.classList.contains('is-visible') &&
            !this.banner.hasAttribute('hidden')) {
          this.banner.setAttribute('hidden', '');
          this.banner.removeEventListener('transitionend', onEnd);
        }
      }, 600);
    }

    openModal() {
      if (!this.modal) return;
      this.refreshTogglesFromConsent();
      this.modal.removeAttribute('hidden');
      this.modal.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      this._releaseFocus = trap(this.modal, this._lastTrigger || document.activeElement);
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.setAttribute('aria-hidden', 'true');
      this.modal.setAttribute('hidden', '');
      document.documentElement.style.overflow = '';
      if (this._releaseFocus) {
        this._releaseFocus();
        this._releaseFocus = null;
      }
    }

    saveConsent(consent) {
      // Resolve the API live: it may have loaded since our initial probe.
      const api = this.api || getPrivacyApi();
      if (api) this.api = api;

      // The visitor has made a choice: stop the banner from nagging and let
      // listeners react. Done regardless of whether the API is up yet.
      writeCookie(SEEN_COOKIE, '1');
      this.hideBanner();
      this.closeModal();
      // Local mirror read by the in-theme pixel-loaders fallback.
      try {
        localStorage.setItem('athl8_consent', JSON.stringify(consent));
      } catch (_) { /* ignore quota / disabled storage */ }
      document.dispatchEvent(new CustomEvent('athl8:consent-changed', { detail: consent }));

      if (api) {
        // Authoritative path. Shopify maps these granular keys to Consent
        // Mode v2 / CCPA signals that the Meta app pixel, Google, and Shopify
        // Analytics read. setTrackingConsent is what ungates them. Clear any
        // pending record once it lands.
        api.setTrackingConsent(consent, () => {
          try { localStorage.removeItem(PENDING_KEY); } catch (_) {}
        });
      } else {
        // API not reachable yet (slow load, or Customer Privacy not enabled
        // in Admin). Stash the choice so onPrivacyReady()/syncPendingConsent()
        // can replay it into setTrackingConsent the moment the API appears.
        // Consent is never stranded where the Shopify-managed pixels cannot
        // read it.
        try {
          localStorage.setItem(PENDING_KEY, JSON.stringify(consent));
        } catch (_) { /* ignore quota / disabled storage */ }
      }
    }
  }

  if (!customElements.get('cookie-consent')) {
    customElements.define('cookie-consent', CookieConsent);
  }
})();
